import { useEffect, useRef, useState, useMemo } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

//UI COMPONENTS
import CollapsableArrow from "../../../CollapsableArrow/CollapsableArrow";
import Skeleton from "../../../Skeleton/Skeleton";

//ASSETS
import searchIcon from "../../../../assets/images/icons/search-icon.svg";
import clockIcon from "../../../../pages/ActivityPage/ui/ActivityTable/icons/states/clock-icon.svg";
import EmptyStar from "../../../../assets/images/icons/empty-star-icon.svg?react";
import YellowStar from "../../../../assets/images/icons/filled-star-icon.svg?react";

//STYLES
import styles from "./AccountsSelector.module.scss";

//UTILS
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  updateAccounts,
  updateAccountSelected,
} from "../../../../store/slices/userAccountsSlice";
import { setFavoriteAccount } from "../../../../utils/favoriteAccount";

//TYPES
import { UserAccount } from "../../../../types/types";

const AccountsSelector = () => {
  const {
    accountSelectedItem,
    active,
    inputIcon,
    inputWrapper,
    item,
    itemText,
    list,
    mirrorAccountContainer,
    mirrorInProcessContainer,
    noResults,
    popover,
    searchInput,
    selectorContainer,
    singleAccContainer,
    trigger,
    starButton,
  } = styles;

  const { accounts, accountSelected } = useAppSelector(
    (state) => state.userAccounts
  );
  const { user } = useAppSelector((state) => state.authentication);

  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);

  // Local state for temporary accounts (while dropdown is open)
  const [localAccounts, setLocalAccounts] = useState<UserAccount[]>([]);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const handleChangeAccount = (accountNumber: string) => {
    const account = (hasPendingChanges ? localAccounts : accounts).find(
      (acc) => acc.number == accountNumber
    );
    if (account) {
      dispatch(updateAccountSelected(account));
      setIsOpen(false);
      setSearchValue("");
    }
  };

  const handleToggleDropdown = () => {
    const willOpen = !isOpen;

    // On open, copy Redux accounts to local state
    if (willOpen) {
      setLocalAccounts([...accounts]);
      setHasPendingChanges(false);
    }

    setIsOpen(willOpen);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleToggleFavorite = (e: React.MouseEvent, accountNumber: string) => {
    e.stopPropagation();

    // Update ONLY local state (not Redux yet)
    const accountsWithFavorite = localAccounts.map((account) => {
      if (account.number === accountNumber) {
        return { ...account, isFavorite: !account.isFavorite };
      }
      return { ...account, isFavorite: false };
    });

    // Save to localStorage immediately
    setFavoriteAccount({
      accountNumber:
        accountsWithFavorite.find((acc) => acc.isFavorite)?.number || null,
      email: user?.email ?? "",
    });

    // Update local state
    setLocalAccounts(accountsWithFavorite);
    setHasPendingChanges(true);
  };

  // Use localAccounts if there are pending changes, otherwise use Redux accounts
  const getAccountsToDisplay = () => {
    return hasPendingChanges ? localAccounts : accounts;
  };

  const filteredAccounts = () => {
    const accountsToFilter = getAccountsToDisplay();

    if (searchValue.length > 0) {
      const value = searchValue.toLowerCase();
      return accountsToFilter.filter(
        (account) =>
          account.number.toLowerCase().includes(value) ||
          account.name.toLowerCase().includes(value)
      );
    }

    return accountsToFilter;
  };

  const calculateListHeight = useMemo(() => {
    const itemCount = filteredAccounts().length;

    const ITEM_HEIGHT = 66;

    const MIN_HEIGHT = 132;
    const MAX_HEIGHT = 400;

    const calculatedHeight = itemCount * ITEM_HEIGHT;

    return Math.min(Math.max(calculatedHeight, MIN_HEIGHT), MAX_HEIGHT);
  }, [filteredAccounts().length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const newIndex = Math.min(
        highlightIndex + 1,
        filteredAccounts().length - 1
      );
      setHighlightIndex(newIndex);
      // Scroll to highlighted item
      virtuosoRef.current?.scrollToIndex({
        index: newIndex,
        align: "center",
        behavior: "smooth",
      });
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const newIndex = Math.max(highlightIndex - 1, 0);
      setHighlightIndex(newIndex);
      // Scroll to highlighted item
      virtuosoRef.current?.scrollToIndex({
        index: newIndex,
        align: "center",
        behavior: "smooth",
      });
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredAccounts()[highlightIndex]) {
        handleChangeAccount(filteredAccounts()[highlightIndex].number);
      }
    }
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  // On dropdown close, apply changes and reorder
  useEffect(() => {
    if (!isOpen && hasPendingChanges) {
      // 1. Sort: favorite first, rest alphabetically
      const sortedAccounts = [...localAccounts].sort((a, b) => {
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;
        return a.name.localeCompare(b.name, "es", { sensitivity: "base" });
      });

      // 2. Update Redux
      dispatch(updateAccounts(sortedAccounts));

      // 3. Clear local state
      setHasPendingChanges(false);
      setLocalAccounts([]);
    }
  }, [isOpen, hasPendingChanges, localAccounts, dispatch]);

  useEffect(() => {
    if (filteredAccounts().length > 0) {
      setHighlightIndex(0);
    }
  }, [searchValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Render function for each item in virtualized list
  const ItemContent = (index: number, acc: UserAccount) => {
    return (
      <li
        key={acc.number}
        className={`${item} ${index === highlightIndex ? active : ""}`}
        onMouseEnter={() => setHighlightIndex(index)}
        onClick={() => handleChangeAccount(acc.number)}
        style={{
          boxSizing: "border-box",
        }}
      >
        <div
          className={`${itemText} ${
            accountSelected?.number == acc.number ? accountSelectedItem : ""
          }`}
        >
          <p>{acc.name}</p>
          <span>#{acc.number}</span>
        </div>
        <div
          className={starButton}
          onClick={(e) => handleToggleFavorite(e, acc.number)}
        >
          {acc.isFavorite ? <YellowStar /> : <EmptyStar />}
        </div>
        {[
          "ONBOARDING_IN_PROCESS",
          "IN_PROCESS",
          "ACCOUNT_CREATION_PENDING",
        ].includes(acc.unifyState) && (
          <div className={mirrorInProcessContainer}>
            <img src={clockIcon} alt="clock icon" />
            Unificación en proceso
          </div>
        )}
        {acc.unifyState === "MIRRORED" && (
          <div className={mirrorAccountContainer}>Unificada</div>
        )}
      </li>
    );
  };

  return (
    <div className={selectorContainer} ref={containerRef}>
      <div
        className={`${trigger} ${
          !accountSelected || accountSelected?.inSinglAccount
            ? singleAccContainer
            : ""
        }`}
        onClick={handleToggleDropdown}
      >
        {accountSelected ? (
          <p>
            {accountSelected.name} <span>#{accountSelected.number}</span>
          </p>
        ) : (
          <Skeleton width="400px" height="19px" />
        )}
        {!!accountSelected &&
          [
            "ONBOARDING_IN_PROCESS",
            "IN_PROCESS",
            "ACCOUNT_CREATION_PENDING",
          ].includes(accountSelected?.unifyState) && (
            <div className={mirrorInProcessContainer}>
              <img src={clockIcon} alt="clock icon" />
              Unificación en proceso
            </div>
          )}

        {!!accountSelected && accountSelected?.unifyState == "MIRRORED" && (
          <div className={mirrorAccountContainer}>Unificada</div>
        )}
        {!!accountSelected && !accountSelected?.inSinglAccount && (
          <CollapsableArrow isOpen={isOpen} />
        )}
      </div>

      {isOpen && (
        <div className={popover}>
          <div className={inputWrapper}>
            <span className={inputIcon}>
              <img src={searchIcon} alt="search icon" />
            </span>
            <input
              ref={inputRef}
              type="text"
              className={searchInput}
              placeholder="Buscar comitentes"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          {filteredAccounts().length > 0 ? (
            <Virtuoso
              ref={virtuosoRef}
              style={{ height: `${calculateListHeight}px` }}
              data={filteredAccounts()}
              itemContent={ItemContent}
              className={list}
            />
          ) : (
            <ul className={list}>
              <li className={noResults}>No se encontraron cuentas</li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountsSelector;
